import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { 
  Moon, ArrowLeft, ArrowRight, Star, Sun, Sparkles, BookOpen, 
  User, Heart, Briefcase, Flame, ChevronDown, ChevronUp, 
  Compass, Shield, Target, Flower2, CircleDot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { fetchNakshatra, fetchNakshatras, fetchCurrentMoon } from "@/lib/api";

// Quick Reference Card Component
function QuickReference({ nakshatra }) {
  const n = nakshatra;
  const referenceItems = [
    { label: "Meaning", value: n.meaning || "The " + n.name },
    { label: "Ruler", value: n.planetary_ruler },
    { label: "Deity", value: n.deity },
    { label: "Symbol", value: n.symbolism },
    { label: "Zodiac", value: n.zodiac_range || `${n.index <= 4 ? 'Aries' : n.index <= 8 ? 'Taurus/Gemini' : 'Various'}` },
    { label: "Gana", value: n.gana || "Deva" },
    { label: "Animal", value: n.animal || "Various" },
    { label: "Element", value: n.element || "Mixed" },
  ];

  return (
    <div className="rounded-xl p-6 mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {referenceItems.map((item) => (
          <div key={item.label} className="text-center sm:text-left">
            <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-1">{item.label}</p>
            <p className="text-white/80 text-sm font-[Cormorant_Garamond]">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Collapsible Section Component
function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, id }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-6" id={id}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 border-b border-white/10 hover:border-white/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-amber-400/70" />
          <h2 className="text-xl font-[Cormorant_Garamond] text-white/90">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="py-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Pada Card Component
function PadaCard({ pada, number, isActive, nakshatra }) {
  const [expanded, setExpanded] = useState(isActive);
  
  // Calculate degree range based on nakshatra index and pada number
  const startDegree = ((nakshatra.index - 1) * 13.333 + (number - 1) * 3.333) % 30;
  const endDegree = ((nakshatra.index - 1) * 13.333 + number * 3.333) % 30;
  const sign = getZodiacSign(nakshatra.index, number);
  const navamsa = getNavamsaSign(nakshatra.index, number);
  const navamsaRuler = getNavamsaRuler(navamsa);
  
  return (
    <div 
      className={`rounded-lg transition-all duration-300 ${isActive ? 'ring-1 ring-emerald-400/30' : ''}`}
      style={{ 
        background: isActive ? "rgba(16,185,129,0.05)" : "rgba(255,255,255,0.02)", 
        border: isActive ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(255,255,255,0.06)" 
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(251,191,36,0.1)" }}>
            <span className="text-amber-400 font-medium">{number}</span>
          </div>
          <div>
            <p className="text-white/90 font-medium">Pada {number}</p>
            <p className="text-white/40 text-xs">{startDegree.toFixed(0)}°{startDegree < 10 ? '0' : ''} - {endDegree.toFixed(0)}°{endDegree < 10 ? '0' : ''} {sign}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActive && (
            <span className="text-emerald-400 text-xs px-2 py-1 rounded-full bg-emerald-400/10">Active</span>
          )}
          <div className="text-right mr-2">
            <p className="text-white/60 text-sm">{navamsa} Navamsa</p>
            <p className="text-white/40 text-xs">Ruled by {navamsaRuler}</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
        </div>
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-white/5 pt-4">
              <div className="prose-dark">
                <MarkdownRenderer content={pada} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper functions for zodiac calculations
function getZodiacSign(nakshatraIndex, pada) {
  const signs = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
  const startDegree = (nakshatraIndex - 1) * 13.333 + (pada - 1) * 3.333;
  const signIndex = Math.floor(startDegree / 30);
  return signs[signIndex % 12];
}

function getNavamsaSign(nakshatraIndex, pada) {
  // Navamsa calculation: Each nakshatra has 4 padas, 9 navamsas per sign
  const navamsas = [
    "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius",
    "Capricorn", "Aquarius", "Pisces", "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
    "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces", "Aries", "Taurus", "Gemini",
    "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"
  ];
  const totalPada = (nakshatraIndex - 1) * 4 + pada;
  return navamsas[(totalPada - 1) % 36];
}

function getNavamsaRuler(sign) {
  const rulers = {
    "Aries": "Mars", "Taurus": "Venus", "Gemini": "Mercury", "Cancer": "Moon",
    "Leo": "Sun", "Virgo": "Mercury", "Libra": "Venus", "Scorpio": "Mars",
    "Sagittarius": "Jupiter", "Capricorn": "Saturn", "Aquarius": "Saturn", "Pisces": "Jupiter"
  };
  return rulers[sign] || "Unknown";
}

// Markdown renderer for content
function MarkdownRenderer({ content }) {
  if (!content) return null;
  
  const html = content
    .replace(/\*\*(.*?)\*\*/g, "<strong class='text-white/90'>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/^### (.*$)/gm, '<h3 class="text-lg text-white/80 font-[Cormorant_Garamond] mb-2 mt-4">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl text-white/90 font-[Cormorant_Garamond] mb-3 mt-5">$1</h2>')
    .replace(/\n\n/g, '</p><p class="text-white/60 leading-relaxed mb-4">')
    .replace(/\n/g, "<br/>");
    
  return (
    <div 
      className="prose-dark text-white/60 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: `<p class="mb-4">${html}</p>` }}
    />
  );
}

// Trait List Component
function TraitList({ traits, type }) {
  const bgColor = type === "strength" ? "rgba(16,185,129,0.05)" : "rgba(239,68,68,0.05)";
  const borderColor = type === "strength" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)";
  const dotColor = type === "strength" ? "bg-emerald-400" : "bg-red-400/70";
  
  return (
    <div className="space-y-3">
      {traits.map((trait, i) => (
        <div 
          key={i} 
          className="rounded-lg p-4"
          style={{ background: bgColor, border: `1px solid ${borderColor}` }}
        >
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full ${dotColor} mt-2 flex-shrink-0`} />
            <div>
              <p className="text-white/90 font-medium mb-1">{trait.split(':')[0] || trait.split(' - ')[0] || trait}</p>
              {(trait.includes(':') || trait.includes(' - ')) && (
                <p className="text-white/50 text-sm">{trait.split(':')[1] || trait.split(' - ')[1]}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Navigation Component
function NakshatraNavigation({ currentIndex, allNakshatras }) {
  const prevNak = currentIndex > 1 ? allNakshatras.find(n => n.index === currentIndex - 1) : allNakshatras.find(n => n.index === 27);
  const nextNak = currentIndex < 27 ? allNakshatras.find(n => n.index === currentIndex + 1) : allNakshatras.find(n => n.index === 1);
  
  return (
    <div className="flex items-center justify-between py-6 border-t border-white/10 mt-12">
      {prevNak && (
        <Link
          to={`/nakshatras/${prevNak.slug}`}
          className="flex items-center gap-2 text-white/40 hover:text-white/80 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <div className="text-right">
            <p className="text-[10px] tracking-[0.2em] uppercase">Previous</p>
            <p className="text-sm text-white/70">{prevNak.name}</p>
          </div>
        </Link>
      )}
      
      <Link
        to="/nakshatras"
        className="flex items-center gap-2 px-4 py-2 rounded-full text-white/40 hover:text-white/80 hover:bg-white/5 transition-all"
      >
        <Compass className="w-4 h-4" />
        <span className="text-sm">All 27</span>
      </Link>
      
      {nextNak && (
        <Link
          to={`/nakshatras/${nextNak.slug}`}
          className="flex items-center gap-2 text-white/40 hover:text-white/80 transition-colors group"
        >
          <div className="text-left">
            <p className="text-[10px] tracking-[0.2em] uppercase">Next</p>
            <p className="text-sm text-white/70">{nextNak.name}</p>
          </div>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      )}
    </div>
  );
}

// Main Component
export default function NakshatraDetailExpanded() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [nakshatra, setNakshatra] = useState(null);
  const [allNakshatras, setAllNakshatras] = useState([]);
  const [currentMoon, setCurrentMoon] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const highlightPada = searchParams.get("pada");

  useEffect(() => {
    async function load() {
      try {
        const [nak, naks, moon] = await Promise.all([
          fetchNakshatra(slug),
          fetchNakshatras(),
          fetchCurrentMoon().catch(() => null)
        ]);
        setNakshatra(nak);
        setAllNakshatras(naks);
        setCurrentMoon(moon);
      } catch (e) {
        console.error("Failed to load nakshatra:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  // Scroll to pada if specified
  useEffect(() => {
    if (highlightPada && !loading) {
      setTimeout(() => {
        const element = document.getElementById(`pada-section`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [highlightPada, loading]);

  // Update document title for SEO
  useEffect(() => {
    if (nakshatra) {
      document.title = `${nakshatra.name} Nakshatra: Traits, Mythology, Symbolism & 4 Padas | Daily Nakshatra`;
    }
    return () => {
      document.title = 'Daily Nakshatra - Vedic Astrology';
    };
  }, [nakshatra]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
      </div>
    );
  }

  if (!nakshatra) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-4">
        <p className="text-white/40">Nakshatra not found.</p>
        <Link to="/nakshatras">
          <Button variant="ghost" className="text-white/60">View All Nakshatras</Button>
        </Link>
      </div>
    );
  }

  const isActive = currentMoon?.nakshatra_slug === slug;
  const activePada = isActive ? currentMoon?.pada : parseInt(highlightPada) || null;

  // Parse birth traits into sections if available
  const birthTraitsContent = nakshatra.birth_traits || "";

  return (
    <div className="min-h-screen bg-[#050505]">

      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/nakshatras" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase hidden sm:inline">All Nakshatras</span>
            <span className="text-sm tracking-wider uppercase sm:hidden">Back</span>
          </Link>
          <div className="flex items-center gap-3">
            {isActive && (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 moon-pulse" />
                <span className="text-emerald-400">Moon is here</span>
              </span>
            )}
            <span className="text-sm text-white/40 tracking-wider uppercase font-[Manrope]">
              {nakshatra.index} / 27
            </span>
          </div>
        </div>
      </nav>

      <main className="pt-14">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-900/5 via-transparent to-transparent" />
          
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Nakshatra Number Badge */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-amber-400/60 text-sm tracking-[0.3em] uppercase">Nakshatra {nakshatra.index}</span>
                <div className="h-px flex-1 bg-gradient-to-r from-amber-400/20 to-transparent" />
              </div>

              {/* Main Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-[Cormorant_Garamond] font-light text-white/95 mb-4" data-testid="nakshatra-title">
                {nakshatra.name}
              </h1>
              
              {/* Subtitle */}
              <p className="text-xl sm:text-2xl text-white/50 font-[Cormorant_Garamond] italic mb-8">
                {nakshatra.meaning || `The Star of ${nakshatra.deity}`}
              </p>

              {/* Quick visual summary */}
              <QuickReference nakshatra={nakshatra} />

              {/* Overview */}
              <div className="prose-dark max-w-3xl">
                <p className="text-white/60 leading-relaxed text-lg">
                  {nakshatra.overview || `${nakshatra.name} is the ${getOrdinal(nakshatra.index)} nakshatra in the Vedic zodiac, ruled by ${nakshatra.planetary_ruler} and presided over by ${nakshatra.deity}. Its symbol, the ${nakshatra.symbolism}, represents the core archetype of this lunar mansion.`}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content Sections */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          
          {/* Birth Traits Section */}
          <CollapsibleSection title="Birth Traits" icon={User} defaultOpen={true} id="birth-traits">
            <div className="space-y-8">
              {/* Core Personality */}
              <div>
                <h3 className="text-lg text-white/80 font-[Cormorant_Garamond] mb-4 flex items-center gap-2">
                  <CircleDot className="w-4 h-4 text-amber-400/60" />
                  Core Personality
                </h3>
                <MarkdownRenderer content={birthTraitsContent} />
              </div>

              {/* Strengths & Challenges Grid */}
              {nakshatra.strengths && nakshatra.challenges && (
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  <div>
                    <h4 className="text-white/70 text-sm tracking-wider uppercase mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-400/60" />
                      Natural Strengths
                    </h4>
                    <TraitList traits={nakshatra.strengths} type="strength" />
                  </div>
                  <div>
                    <h4 className="text-white/70 text-sm tracking-wider uppercase mb-4 flex items-center gap-2">
                      <Target className="w-4 h-4 text-red-400/60" />
                      Potential Challenges
                    </h4>
                    <TraitList traits={nakshatra.challenges} type="challenge" />
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Mythology Section */}
          <CollapsibleSection title="Mythology" icon={BookOpen} id="mythology">
            <div className="max-w-3xl">
              <MarkdownRenderer content={nakshatra.mythology} />
            </div>
          </CollapsibleSection>

          {/* Symbolism Section */}
          <CollapsibleSection title="Symbolism" icon={Sparkles} id="symbolism">
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Primary Symbol */}
              <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-3 mb-3">
                  <Star className="w-5 h-5 text-amber-400/70" />
                  <h4 className="text-white/80 font-medium">Primary Symbol</h4>
                </div>
                <p className="text-white/90 font-[Cormorant_Garamond] text-lg mb-2">{nakshatra.symbolism}</p>
                <p className="text-white/50 text-sm">
                  {nakshatra.symbol_meaning || `The ${nakshatra.symbolism} represents the essential nature of ${nakshatra.name}`}
                </p>
              </div>

              {/* Planetary Ruler */}
              <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-3 mb-3">
                  <Sun className="w-5 h-5 text-amber-400/70" />
                  <h4 className="text-white/80 font-medium">Planetary Ruler</h4>
                </div>
                <p className="text-white/90 font-[Cormorant_Garamond] text-lg mb-2">{nakshatra.planetary_ruler}</p>
                <p className="text-white/50 text-sm">
                  {nakshatra.ruler_meaning || `${nakshatra.planetary_ruler} influences the characteristics and tendencies of ${nakshatra.name} natives`}
                </p>
              </div>

              {/* Presiding Deity */}
              <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-3 mb-3">
                  <Flame className="w-5 h-5 text-amber-400/70" />
                  <h4 className="text-white/80 font-medium">Presiding Deity</h4>
                </div>
                <p className="text-white/90 font-[Cormorant_Garamond] text-lg mb-2">{nakshatra.deity}</p>
                <p className="text-white/50 text-sm">
                  {nakshatra.deity_meaning || `The energy and qualities of ${nakshatra.deity} permeate this nakshatra`}
                </p>
              </div>

              {/* Shakti */}
              {nakshatra.shakti && (
                <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <Flower2 className="w-5 h-5 text-amber-400/70" />
                    <h4 className="text-white/80 font-medium">Shakti (Power)</h4>
                  </div>
                  <p className="text-white/90 font-[Cormorant_Garamond] text-lg mb-2">{nakshatra.shakti}</p>
                  <p className="text-white/50 text-sm">{nakshatra.shakti_meaning || "The special power granted by this nakshatra"}</p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Padas Section */}
          <CollapsibleSection title="The Four Padas" icon={Moon} defaultOpen={!!activePada} id="pada-section">
            <div className="mb-6">
              <p className="text-white/50 text-sm mb-6">
                Every nakshatra spans 13°20' and is divided into four quarters of 3°20' each, traditionally called Padas. 
                Each pada falls in a different navamsa (ninth division) sign, adding unique coloring to the nakshatra's expression.
              </p>
            </div>
            
            <div className="space-y-4">
              {[1, 2, 3, 4].map((num) => {
                const padaContent = nakshatra[`pada_${num}`];
                return (
                  <PadaCard
                    key={num}
                    pada={padaContent}
                    number={num}
                    isActive={activePada === num}
                    nakshatra={nakshatra}
                  />
                );
              })}
            </div>
          </CollapsibleSection>

          {/* Relationships & Career (Combined) */}
          {(nakshatra.relationships || nakshatra.career) && (
            <CollapsibleSection title="Life Areas" icon={Heart} id="life-areas">
              <div className="space-y-8">
                {nakshatra.relationships && (
                  <div>
                    <h4 className="text-white/70 text-sm tracking-wider uppercase mb-4 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-rose-400/60" />
                      Relationships & Love
                    </h4>
                    <MarkdownRenderer content={nakshatra.relationships} />
                  </div>
                )}
                {nakshatra.career && (
                  <div>
                    <h4 className="text-white/70 text-sm tracking-wider uppercase mb-4 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-400/60" />
                      Career & Talents
                    </h4>
                    <MarkdownRenderer content={nakshatra.career} />
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Navigation */}
          <NakshatraNavigation currentIndex={nakshatra.index} allNakshatras={allNakshatras} />
        </section>
      </main>
    </div>
  );
}

// Helper function for ordinal numbers
function getOrdinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
